import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Modal,
  Typography,
} from "@mui/material";
import { CloseOutlined } from "@ant-design/icons";
import { Image } from "antd";
import useAxios from "../../utils/useAxios";
import { getLastAssetUpdate } from "../../features/Logistics/models/logisticsProgressPayload";

export default function ComponentInfo({ component, open, onClose }) {
  const api = useAxios();

  const [imageUrls, setImageUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastModifiedBy, setLastModifiedBy] = useState(null);

  const lastUpdate = useMemo(
    () =>
      getLastAssetUpdate({ ...component, last_modified_by: lastModifiedBy }),
    [component, lastModifiedBy],
  );

  useEffect(() => {
    if (!component?.id) return;

    const fetchAll = async () => {
      setLoading(true);
      try {
        const [imagesRes, detailRes] = await Promise.all([
          api.get(`/component-images/?component=${component.id}`),
          api.get(`/panel-components/${component.id}/`),
        ]);
        setImageUrls(imagesRes.data.results.map((img) => img.image_url));
        setLastModifiedBy(detailRes.data?.last_modified_by || null);
      } catch (error) {
        console.error("Error fetching component info:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [component?.id]);

  const handleClose = () => {
    setImageUrls([]);
    setLastModifiedBy(null);
    onClose();
  };

  const InfoBox = ({ label, value, xs = 6 }) => (
    <Grid item xs={xs}>
      <Box
        sx={{
          p: 2,
          borderRadius: "10px",
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "grey.50",
          height: "100%",
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600 }}
        >
          {label}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {value || "—"}
        </Typography>
      </Box>
    </Grid>
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="component-info-modal-title"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", md: 700 },
          bgcolor: "background.paper",
          boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
          borderRadius: "20px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2.5,
            bgcolor: "primary.main",
            flexShrink: 0,
          }}
        >
          <Typography
            id="component-info-modal-title"
            variant="h6"
            sx={{ fontWeight: 700, color: "#fff", letterSpacing: 0.3 }}
          >
            {component?.tagNumber} Info
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "#fff",
              "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
            }}
          >
            <CloseOutlined />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ overflowY: "auto", flexGrow: 1, px: 3, py: 2.5 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Image Gallery */}
              <Box
                sx={{
                  width: "100%",
                  height: 240,
                  overflow: "hidden",
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: "divider",
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "grey.50",
                }}
              >
                {imageUrls.length > 0 ? (
                  <Image.PreviewGroup
                    preview={{ zIndex: 2000 }}
                    items={imageUrls}
                  >
                    <Image
                      src={imageUrls[0]}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Image.PreviewGroup>
                ) : (
                  <Typography variant="body2" color="text.disabled">
                    No images available
                  </Typography>
                )}
              </Box>

              {/* Details */}
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  color: "primary.main",
                  mb: 1.5,
                }}
              >
                Details
              </Typography>
              <Grid container spacing={2}>
                <InfoBox
                  label="Tag Number"
                  value={component?.tagNumber}
                  xs={6}
                />
                <InfoBox
                  label="Previous Tag Number"
                  value={component?.prevTagNumber}
                  xs={6}
                />
                <InfoBox label="Name" value={component?.componentName} xs={6} />
                <InfoBox
                  label="Serial Number"
                  value={component?.serialNumber}
                  xs={6}
                />
                <InfoBox
                  label="Part Number"
                  value={component?.partNumber}
                  xs={12}
                />
                <InfoBox
                  label="Description"
                  value={component?.description}
                  xs={12}
                />
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "10px",
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "grey.50",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Related Assets
                    </Typography>
                    {component?.relatedTo?.length > 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        {component.relatedTo.map((eq) => (
                          <Box
                            key={eq.id}
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: "8px",
                              border: "1px solid",
                              borderColor: "primary.light",
                              bgcolor: "primary.50",
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "primary.main" }}
                            >
                              {eq.tagNumber}
                            </Typography>
                            {eq.equipmentName && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {eq.equipmentName}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        —
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <InfoBox label="Created" value={component?.created} xs={6} />
                <InfoBox label="Last Updated" value={lastUpdate?.at} xs={6} />
                <InfoBox
                  label="Source"
                  value={lastUpdate?.sourceLabel}
                  xs={6}
                />
                <InfoBox
                  label="Created by"
                  value={component?.created_by}
                  xs={6}
                />
                <InfoBox label="Updated By" value={lastUpdate?.by} xs={6} />
              </Grid>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
